import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Button, Result, Spin } from 'antd';
import { useAuth } from './context/AuthContext';
import AppLayout from './layouts/AppLayout';
import Login from './pages/Login';
import ChangePassword from './pages/ChangePassword';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Impersonate from './pages/Impersonate';
import Dashboard from './pages/Dashboard';
import Calls from './pages/Calls';
import Scheduling from './pages/Scheduling';
import Inbox from './pages/Inbox';
import Todos from './pages/Todos';
import Reports from './pages/Reports';
import Contacts from './pages/Contacts';
import ContactProfile from './pages/Contacts/Profile';
import Property from './pages/Property';
import Pipelines from './pages/Pipelines';
import Settings from './pages/Settings';
import Stub from './pages/Stub';
import {
  faBolt
} from '@fortawesome/free-solid-svg-icons';

function PrivateRoute({ children }) {
  const { isAuthenticated, loading, offline, retry, mustResetPassword } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }
  if (offline) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100vh' }}>
        <Result
          status="warning"
          title="We can't reach the server"
          subTitle="Check your connection and try again."
          extra={
            <Button type="primary" onClick={retry}>
              Try again
            </Button>
          }
        />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location }} replace />;
  if (mustResetPassword && !localStorage.getItem('impersonateOrg')) return <ChangePassword firstTime />;
  return children;
}

function Redirect({ to, type }) {
  const { search, state } = useLocation();
  const params = new URLSearchParams(search);
  if (type) params.set('type', type);
  const qs = params.toString();
  return <Navigate to={qs ? `${to}?${qs}` : to} state={state} replace />;
}

function RequirePerm({ perm, children }) {
  const { can } = useAuth();
  return can(perm) ? children : <Navigate to="/" replace />;
}

function NoStaff({ children }) {
  const { user } = useAuth();
  return user?.role === 'staff' ? <Navigate to="/" replace /> : children;
}

function RequireAgentZero({ children }) {
  const { isPlatformAdmin, previewRole } = useAuth();
  return isPlatformAdmin && !previewRole ? children : <Navigate to="/" replace />;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/impersonate" element={<Impersonate />} />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <AppLayout />
          </PrivateRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="todos" element={<Todos />} />
        <Route path="calls" element={<Calls />} />
        <Route path="contacts" element={<Contacts />} />
        <Route path="contacts/:kind/:id" element={<ContactProfile />} />
        <Route path="prospects" element={<Redirect to="/contacts" type="prospect" />} />
        <Route path="clients" element={<Redirect to="/contacts" type="tenant" />} />
        <Route path="tenants" element={<Redirect to="/contacts" type="tenant" />} />
        <Route path="owners" element={<Redirect to="/contacts" type="owner" />} />
        <Route path="vendors" element={<Redirect to="/contacts" type="vendor" />} />
        <Route path="property" element={<Property />} />
        <Route path="properties" element={<Redirect to="/property" />} />
        <Route path="pipelines" element={<NoStaff><Pipelines /></NoStaff>} />
        <Route path="scheduling" element={<NoStaff><Scheduling /></NoStaff>} />
        <Route path="maintenance" element={<Redirect to="/pipelines?tab=maintenance" />} />
        <Route path="inbox" element={<Inbox />} />
        <Route path="messages" element={<Redirect to="/inbox" />} />
        <Route path="reports" element={<RequirePerm perm="view_reports"><Reports /></RequirePerm>} />
        <Route path="settings" element={<RequireAgentZero><Settings /></RequireAgentZero>} />
        <Route
          path="automations"
          element={<Stub title="Automations" subtitle="Rules and SLA thresholds" icon={faBolt} />}
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
